import type { OrganizationScope } from "@/types/organization-scope"
import { isScopeSwitchableForPermissions } from "@/utils/hqMenuAccess"
import { isOrganizationScopeSwitchable } from "@/utils/organizationScopeSwitch"

export const ORGANIZATION_SELECT_PATH = "/organization-select"
export const NO_ORGANIZATION_PATH = "/no-organization"

export function isHqRoutePath(path: string) {
  return path === "/hq" || path.startsWith("/hq/")
}

export function isHqScopeId(scopeId: string) {
  return scopeId.startsWith("chain-headquarter-") || scopeId.startsWith("chain-region-")
}

export function isHqScope(scope: OrganizationScope | undefined, scopeId = "") {
  return scope?.type === "chain-headquarter" || scope?.type === "chain-region" || isHqScopeId(scopeId)
}

function normalizePositiveId(value: unknown) {
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : undefined
}

export function resolveScopedShopIdForPermissionProbe({
  activeScope,
  activeScopeId,
  legacyShopId,
}: {
  activeScope?: OrganizationScope
  activeScopeId: string
  legacyShopId?: unknown
}) {
  if (isHqScope(activeScope, activeScopeId)) return undefined
  return normalizePositiveId(activeScope?.meta?.shopId)
    ?? normalizePositiveId(legacyShopId)
}

export function shouldBootstrapShopOnlyApis({
  path,
  activeScope,
  activeScopeId,
}: {
  path: string
  activeScope?: OrganizationScope
  activeScopeId: string
}) {
  return !isHqRoutePath(path) && !isHqScope(activeScope, activeScopeId)
}

export function isOrganizationSelectPath(path: string) {
  return path === ORGANIZATION_SELECT_PATH
}

export function isNoOrganizationPath(path: string) {
  return path === NO_ORGANIZATION_PATH
}

export function isShopOnlyRoutePath(path: string) {
  const normalizedPath = path.split(/[?#]/, 1)[0]
  return normalizedPath === "/business"
    || normalizedPath.startsWith("/business/")
    || normalizedPath === "/dashboard"
    || normalizedPath.startsWith("/dashboard/")
    || normalizedPath === "/setting"
    || normalizedPath.startsWith("/setting/")
    || normalizedPath === "/stock"
    || normalizedPath.startsWith("/stock/")
    || normalizedPath === "/procurement"
    || normalizedPath.startsWith("/procurement/")
    || normalizedPath === "/order"
    || normalizedPath.startsWith("/order/")
    || normalizedPath === "/reservation/workbench"
    || normalizedPath.startsWith("/reservation/workbench/")
    || normalizedPath === "/membership"
    || normalizedPath.startsWith("/membership/")
}

export function normalizeOrganizationRedirectPath(value: unknown) {
  if (typeof value !== "string") {
    return "/"
  }
  const path = value.split(/[?#]/, 1)[0]
  if (
    !value.startsWith("/")
    || value.startsWith("//")
    || isOrganizationSelectPath(path)
    || isNoOrganizationPath(path)
    || path === "/403"
    || path === "/404"
  ) {
    return "/"
  }
  return value
}

export function requiresViewScopeRoutePath(path: string) {
  return isHqRoutePath(path)
    || path === "/reservation/workbench"
    || path.startsWith("/reservation/workbench/")
    || path === "/membership"
    || path.startsWith("/membership/")
}

interface ShouldBlockHqRouteOptions {
  path: string
  hasToken: boolean
  organizationsResolved: boolean
  organizations: OrganizationScope[]
}

export function shouldBlockHqRoute({
  path,
  hasToken,
  organizationsResolved,
  organizations,
}: ShouldBlockHqRouteOptions) {
  return isHqRoutePath(path)
    && hasToken
    && organizationsResolved
    && !organizations.some(item => isHqScope(item, item.id) && isOrganizationScopeSwitchable(item))
}

export function resolveHqScopeIdForRoute(
  path: string,
  organizations: OrganizationScope[],
  permissionList: string[] = [],
  options: { allowPendingPermissionHq?: boolean; activeScopeId?: string } = {},
) {
  if (!isHqRoutePath(path)) {
    return ""
  }
  const activeScope = organizations.find(item => item.id === options.activeScopeId)
  const shouldUsePendingPermissionScope = options.allowPendingPermissionHq && permissionList.length === 0
  if (
    activeScope
    && isHqScope(activeScope, activeScope.id)
    && (
      shouldUsePendingPermissionScope
        ? isOrganizationScopeSwitchable(activeScope)
        : isScopeSwitchableForPermissions(activeScope, permissionList)
    )
  ) {
    return activeScope!.id
  }
  if (shouldUsePendingPermissionScope) {
    return organizations.find(item =>
      isHqScope(item, item.id) && isOrganizationScopeSwitchable(item),
    )?.id ?? ""
  }
  return organizations.find(item =>
    isHqScope(item, item.id) && isScopeSwitchableForPermissions(item, permissionList),
  )?.id ?? ""
}

export function resolveOrganizationScopeIdForRedirect(
  path: string,
  organizations: OrganizationScope[],
  preferredScopeId: string,
) {
  if (!isHqRoutePath(path)) {
    return preferredScopeId
  }
  return resolveHqScopeIdForRoute(path, organizations, [], {
    allowPendingPermissionHq: true,
    activeScopeId: preferredScopeId,
  })
}

interface ShouldRedirectToOrganizationSelectOptions {
  path: string
  hasToken: boolean
  organizationsResolved: boolean
  activeScopeId: string
  organizations: OrganizationScope[]
  permissionList?: string[]
  allowPendingPermissionHq?: boolean
}

interface OrganizationAvailabilityOptions {
  hasToken: boolean
  organizationsResolved: boolean
  organizations: OrganizationScope[]
}

interface ShouldRedirectToNoOrganizationOptions extends OrganizationAvailabilityOptions {
  path: string
}

interface ShouldRedirectShopRouteFromHeadquarterScopeOptions {
  path: string
  hasToken: boolean
  organizationsResolved: boolean
  activeScopeId: string
  organizations: OrganizationScope[]
}

interface ShouldReloadOrganizationsForTokenOptions {
  currentToken?: string | null
  organizationsResolved: boolean
  organizationsToken: string
}

export function shouldRedirectToOrganizationSelect({
  path,
  hasToken,
  organizationsResolved,
  activeScopeId,
  organizations,
  permissionList = [],
  allowPendingPermissionHq = false,
}: ShouldRedirectToOrganizationSelectOptions) {
  if (
    !hasToken
    || !organizationsResolved
    || isOrganizationSelectPath(path)
    || isNoOrganizationPath(path)
    || path === "/403"
  ) {
    return false
  }
  const shouldUsePendingPermissionScope = allowPendingPermissionHq && permissionList.length === 0
  const switchableOrganizations = organizations.filter(item =>
    shouldUsePendingPermissionScope
      ? isOrganizationScopeSwitchable(item)
      : isScopeSwitchableForPermissions(item, permissionList),
  )
  if (switchableOrganizations.length === 0) {
    return false
  }
  if (isHqRoutePath(path)) {
    const hqScopeId = resolveHqScopeIdForRoute(path, organizations, permissionList, {
      allowPendingPermissionHq: shouldUsePendingPermissionScope,
      activeScopeId,
    })
    return Boolean(hqScopeId && activeScopeId !== hqScopeId)
  }
  if (!activeScopeId) {
    return true
  }
  return !switchableOrganizations.some(item => item.id === activeScopeId)
}

export function hasNoSwitchableOrganizations({
  hasToken,
  organizationsResolved,
  organizations,
}: OrganizationAvailabilityOptions) {
  return hasToken
    && organizationsResolved
    && !organizations.some(item => isOrganizationScopeSwitchable(item))
}

export function shouldRedirectToNoOrganization({
  path,
  ...availability
}: ShouldRedirectToNoOrganizationOptions) {
  return !isNoOrganizationPath(path) && hasNoSwitchableOrganizations(availability)
}

export function shouldRedirectShopRouteFromHeadquarterScope({
  path,
  hasToken,
  organizationsResolved,
  activeScopeId,
  organizations,
}: ShouldRedirectShopRouteFromHeadquarterScopeOptions) {
  if (!hasToken || !organizationsResolved || !isShopOnlyRoutePath(path)) {
    return false
  }
  const activeScope = organizations.find(item => item.id === activeScopeId)
  return activeScope?.enabled === true && isHqScope(activeScope, activeScopeId)
}

export function shouldReloadOrganizationsForToken({
  currentToken,
  organizationsResolved,
  organizationsToken,
}: ShouldReloadOrganizationsForTokenOptions) {
  if (!currentToken) {
    return false
  }
  return !organizationsResolved || organizationsToken !== currentToken
}
